import {
  isDefined,
  type ProgramTaskData,
  type ProgramTaskDay,
  type ProgramTaskNoSchedule,
  type ProgramTaskSchedule,
  type ProgramTaskSequentialWeekday,
  type ProgramTaskWeekday,
} from '@terros/common'
import { Info } from 'luxon'
import { type SelectItem } from '../../types'
import { type FormProgramTask } from './types'

export const removeDay = (schedule: ProgramTaskWeekday, day: number): ProgramTaskWeekday => {
  return {
    ...schedule,
    days: schedule.days.filter((d) => d.weekday !== day),
  }
}

export const removeWeek = (schedule: ProgramTaskWeekday, day: number, week: number): ProgramTaskWeekday => {
  const thisDay = schedule.days.find((d) => d.weekday === day)
  if (!thisDay) return schedule
  if (thisDay.weeks.length === 1) return removeDay(schedule, day)
  return {
    ...schedule,
    days: schedule.days.map((d) => (d.weekday === day ? { ...d, weeks: d.weeks.filter((w) => w !== week) } : d)),
  }
}

export const addWeek = (schedule: ProgramTaskWeekday, day: number, week: number): ProgramTaskWeekday => {
  const thisDay = schedule.days.find((d) => d.weekday === day)
  if (!thisDay) return { ...schedule, days: [...schedule.days, { weekday: day, weeks: [week] }] }
  return {
    ...schedule,
    days: schedule.days.map((d) => (d.weekday === day ? { ...d, weeks: [...d.weeks, week] } : d)),
  }
}

export const selectAllWeeks = (schedule: ProgramTaskWeekday, day: number, weeks: number): ProgramTaskWeekday => {
  const thisDay = schedule.days.find((d) => d.weekday === day)
  const allWeeks = Array.from({ length: weeks }, (_, i) => i + 1)
  if (!thisDay)
    return {
      ...schedule,
      days: [...schedule.days, { weekday: day, weeks: allWeeks }],
    }
  return {
    ...schedule,
    days: schedule.days.map((d) => (d.weekday === day ? { ...d, weeks: allWeeks } : d)),
  }
}

export const getNewSchedule = (type: ProgramTaskSchedule['type']): ProgramTaskSchedule => {
  switch (type) {
    case 'day':
      return getNewDaySchedule()
    case 'weekday':
      return getNewWeekdaySchedule()
    case 'sequentialWeekday':
      return getNewSequentialWeekdaySchedule()
    case 'none':
      return getNewNoneSchedule()
  }
}

export const getNewDaySchedule = (): ProgramTaskDay => {
  return {
    type: 'day',
    days: [],
  }
}

export const getNewWeekdaySchedule = (): ProgramTaskWeekday => {
  return {
    type: 'weekday',
    days: [],
  }
}

export const getNewNoneSchedule = (): ProgramTaskNoSchedule => {
  return {
    type: 'none',
  }
}

export const getNewSequentialWeekdaySchedule = (): ProgramTaskSequentialWeekday => {
  return {
    type: 'sequentialWeekday',
    weekdays: [],
  }
}

export const TASK_SCHEDULE_TYPE_OPTIONS: SelectItem<ProgramTaskSchedule['type']>[] = [
  {
    value: 'day',
    label: 'Sequential',
  },
  {
    value: 'weekday',
    label: 'Weekdays',
  },
  {
    value: 'sequentialWeekday',
    label: 'Sequential Weekdays',
  },
]

export function hasScheduleFields(task: Pick<ProgramTaskData, 'schedule' | 'duration' | 'openSince'>): boolean {
  const { schedule, duration, openSince } = task

  return schedule.type !== 'none' || isDefined(duration) || isDefined(openSince)
}

export function formTaskHasScheduleFields(task: FormProgramTask): boolean {
  const { schedule, duration, openSince } = task

  return schedule.type !== 'none' || duration.type !== 'none' || openSince !== '00:00'
}

export const WEEKDAY_SELECT_OPTIONS: SelectItem<number>[] = Info.weekdays().map((day, i) => ({
  value: i + 1,
  label: day,
}))
